/**
 * ApiDataProvider — talks to the backend's REST API. Endpoints and JSON shapes are documented in
 * docs/API_CONTRACT.md and docs/openapi.yaml; bodies and responses use the types in src/types/models.ts.
 *
 * If the backend's routes differ, change the paths here — pages and components do not change.
 * For GraphQL or an SDK, write another class implementing DataProvider and select it in services/index.ts.
 */
import type {
  Backup,
  DeadlineChangeInput,
  ImportSummary,
  Member,
  MemberInput,
  ParsedDay,
  Project,
  ProjectInput,
  ProjectPatch,
  ProjectUpdate,
  ProjectUpdateFilter,
  ProjectUpdateInput,
  Settings,
  WorkLog,
  WorkLogFilter,
  WorkLogInput,
} from '@/types/models';
import { DataError, type DataProvider, type ProviderCapabilities } from '@/services/DataProvider';
import type { ManagementAnalytics, ManagementFilter } from '@/domain/analytics/types';
import { HttpClient } from './httpClient';

export interface ApiProviderOptions {
  client: HttpClient;
  /** Set true when the backend implements GET /analytics/management (server-side aggregation). */
  serverAnalytics?: boolean;
}

const enc = encodeURIComponent;

export class ApiDataProvider implements DataProvider {
  readonly mode = 'api' as const;
  readonly capabilities: ProviderCapabilities;
  private readonly http: HttpClient;

  /** Present only when the backend aggregates (POST /analytics/management); otherwise the browser computes the figures. */
  readonly getManagementAnalytics?: (filter: ManagementFilter) => Promise<ManagementAnalytics>;

  constructor(opts: ApiProviderOptions) {
    this.http = opts.client;
    this.capabilities = { backup: true, resetDemo: false, deleteAll: false, serverAnalytics: !!opts.serverAnalytics };
    if (opts.serverAnalytics) this.getManagementAnalytics = (filter) => this.http.post<ManagementAnalytics>('/analytics/management', filter);
  }

  /* Projects */
  listProjects() {
    return this.http.get<Project[]>('/projects');
  }
  async getProject(id: string) {
    try {
      return await this.http.get<Project>(`/projects/${enc(id)}`);
    } catch (e) {
      if (e instanceof DataError && e.status === 404) return null;
      throw e;
    }
  }
  createProject(input: ProjectInput) {
    return this.http.post<Project>('/projects', input);
  }
  updateProject(id: string, patch: ProjectPatch) {
    return this.http.patch<Project>(`/projects/${enc(id)}`, patch);
  }
  deleteProject(id: string) {
    return this.http.delete(`/projects/${enc(id)}`);
  }
  changeDeadline(id: string, input: DeadlineChangeInput) {
    return this.http.post<Project>(`/projects/${enc(id)}/deadline-changes`, input);
  }

  /* Members */
  listMembers() {
    return this.http.get<Member[]>('/members');
  }
  async getMember(id: string) {
    try {
      return await this.http.get<Member>(`/members/${enc(id)}`);
    } catch (e) {
      if (e instanceof DataError && e.status === 404) return null;
      throw e;
    }
  }
  createMember(input: MemberInput) {
    return this.http.post<Member>('/members', input);
  }
  updateMember(id: string, patch: Partial<MemberInput>) {
    return this.http.patch<Member>(`/members/${enc(id)}`, patch);
  }
  deleteMember(id: string) {
    return this.http.delete(`/members/${enc(id)}`);
  }

  /* Work logs */
  listWorkLogs(f: WorkLogFilter = {}) {
    return this.http.get<WorkLog[]>('/work-logs', { from: f.from, to: f.to, projectId: f.projectId, memberId: f.memberId });
  }
  createWorkLog(input: WorkLogInput) {
    return this.http.post<WorkLog>('/work-logs', input);
  }
  updateWorkLog(id: string, patch: Partial<WorkLogInput>) {
    return this.http.patch<WorkLog>(`/work-logs/${enc(id)}`, patch);
  }
  deleteWorkLog(id: string) {
    return this.http.delete(`/work-logs/${enc(id)}`);
  }

  /* Project updates */
  listProjectUpdates(f: ProjectUpdateFilter = {}) {
    return this.http.get<ProjectUpdate[]>('/project-updates', { from: f.from, to: f.to, projectId: f.projectId });
  }
  createProjectUpdate(input: ProjectUpdateInput) {
    return this.http.post<ProjectUpdate>('/project-updates', input);
  }
  updateProjectUpdate(id: string, patch: Partial<ProjectUpdateInput>) {
    return this.http.patch<ProjectUpdate>(`/project-updates/${enc(id)}`, patch);
  }
  deleteProjectUpdate(id: string) {
    return this.http.delete(`/project-updates/${enc(id)}`);
  }

  /* Settings */
  getSettings() {
    return this.http.get<Settings>('/settings');
  }
  updateSettings(patch: Partial<Settings>) {
    return this.http.patch<Settings>('/settings', patch);
  }

  /* Bulk */
  importDailyUpdate(days: ParsedDay[]) {
    return this.http.post<ImportSummary>('/imports/daily-update', { days });
  }
  exportBackup() {
    return this.http.get<Backup>('/backup');
  }
  importBackup(backup: Backup) {
    return this.http.put<void>('/backup', backup);
  }

}
